import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["authenticatedUrl", "signUpSuccessUrl"];
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFronteggStore } from '@frontegg/redux-store';
import { ContextHolder } from '@frontegg/rest-api';
import { useAuth, useAuthRoutes } from '../auth';
import { Provider, FronteggStoreContext } from '../FronteggStoreContext';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultSetLoading = loading => {
  if (loading) {
    document.body.classList.add('frontegg-loading');
  } else {
    document.body.classList.remove('frontegg-loading');
  }
};
const HideChildrenIfFronteggRoutes = ({
  children,
  basename
}) => {
  const routes = useAuthRoutes();
  const uriRef = useRef(window.location.pathname);
  const animationFrameRef = useRef(0);
  const [uri, setUri] = useState(window.location.pathname);
  const checker = useCallback(() => {
    if (uriRef.current !== window.location.pathname) {
      uriRef.current = window.location.pathname;
      setUri(document.location.pathname);
    }
    animationFrameRef.current = window.requestAnimationFrame(checker);
  }, []);
  useEffect(() => {
    document.addEventListener('frontegg_onRedirectTo_fired', () => {
      if (uriRef.current !== window.location.pathname) {
        uriRef.current = window.location.pathname;
        setUri(document.location.pathname);
      }
    });
    window.addEventListener('popstate', () => {
      if (uriRef.current !== window.location.pathname) {
        uriRef.current = window.location.pathname;
        setUri(document.location.pathname);
      }
    });
    animationFrameRef.current = window.requestAnimationFrame(checker);
    return () => {
      window.cancelAnimationFrame(animationFrameRef.current);
    };
  }, [checker, setUri]);
  const calculatedBasename = basename ? basename.endsWith('/') ? basename.substring(0, basename.length - 1) : '' : '';
  const checkRoutes = _objectWithoutPropertiesLoose(routes, _excluded);
  if (Object.values(checkRoutes).map(url => `${calculatedBasename}${url}`).indexOf(uri) !== -1) {
    return null;
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
const FronteggContent = ({
  children,
  app,
  setLoading
}) => {
  var _app$options, _app$options2;
  const {
    isLoading
  } = useAuth(({
    isLoading
  }) => ({
    isLoading
  }));
  const customLoginBox = !app || (app == null ? void 0 : (_app$options = app.options) == null ? void 0 : _app$options.customLoginBox) === true;
  const isNextJS = (app == null ? void 0 : (_app$options2 = app.options) == null ? void 0 : _app$options2.framework) === 'nextjs';
  const alwaysShowChildren = customLoginBox || isNextJS;
  const loadingRef = useRef(undefined);
  if (isLoading !== loadingRef.current && !isNextJS) {
    loadingRef.current = isLoading;
    setLoading(isLoading);
  }
  if (alwaysShowChildren) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  if (isLoading && !isNextJS) {
    return null;
  }
  return /*#__PURE__*/_jsx(HideChildrenIfFronteggRoutes, {
    basename: app == null ? void 0 : app.options.basename,
    children: children
  });
};
export const FronteggStoreProvider = props => {
  var _app$options$contextO, _app$options3, _app$options$previewM, _app$options4, _app$options5, _app$options6;
  const {
    children,
    app,
    contextOptions,
    setLoading = defaultSetLoading,
    alwaysVisibleChildren
  } = props;

  // TODO: make this optionals more readable
  const context = (_app$options$contextO = app == null ? void 0 : (_app$options3 = app.options) == null ? void 0 : _app$options3.contextOptions) != null ? _app$options$contextO : contextOptions;
  if (!context) {
    throw Error('contextOptions must not be null or undefined');
  }
  const previewMode = (_app$options$previewM = app == null ? void 0 : (_app$options4 = app.options) == null ? void 0 : _app$options4.previewMode) != null ? _app$options$previewM : false;
  const authOptions = app != null && (_app$options5 = app.options) != null && _app$options5.authOptions ? _extends({}, app.options.authOptions, {
    hostedLoginBox: app.options.hostedLoginBox
  }) : {
    hostedLoginBox: app == null ? void 0 : (_app$options6 = app.options) == null ? void 0 : _app$options6.hostedLoginBox
  };
  ContextHolder.setContext(context);
  const store = useMemo(() => {
    var _app$name, _app$options7;
    return createFronteggStore({
      context,
      appName: (_app$name = app == null ? void 0 : app.name) != null ? _app$name : 'default'
    }, app, previewMode, authOptions, undefined, false, app == null ? void 0 : (_app$options7 = app.options) == null ? void 0 : _app$options7.urlStrategy);
  }, [app, previewMode]);
  return /*#__PURE__*/_jsxs(Provider, {
    context: FronteggStoreContext,
    store: store,
    children: [alwaysVisibleChildren, /*#__PURE__*/_jsx(FronteggContent, {
      setLoading: setLoading,
      app: app,
      children: children
    })]
  });
};